import { create } from "zustand";
import { AxiosError } from "axios";
import { apiClient } from "@/api/client";
import { publicClient } from "@/api/publicClient";
import useAuthStore from "@/store/authStore";

interface UserData {
  first_name: string;
  last_name: string;
  title: string;
  password: string;
  password_confirmation: string;
  language: string;
  timezone: string;
  token: string;
}

interface Address {
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  country: string;
  zip_postal: string;
}

interface MediaRequestData {
  name: string;
  website: string;
  phone: string;
  industry: string;
  size: string;
  logo: File | null;
  shipping: Address;
  billing: Address;
}

interface OnboardingState {
  userData: Partial<UserData>;
  sendUserData: (data: Partial<UserData>) => Promise<void>;
  createMediaAgency: (agencyData: MediaRequestData) => Promise<void>;
  validateToken: (token: string) => Promise<boolean>;
}

const useOnboardingStore = create<OnboardingState>((set) => ({
  userData: {},

  // Uses publicClient — no auth token exists yet
  validateToken: async (token) => {
    try {
      const res = await publicClient.get(
        `/global-media-agency-invitations/${token}`
      );
      return res.data.data;
    } catch {
      return false;
    }
  },

  // Uses publicClient — user is accepting invite, still no auth token
  sendUserData: async (data) => {
    try {
      const response = await publicClient.post(
        `/global-media-agency-invitations/accept/${data.token}`,
        data
      );
      const auth = useAuthStore.getState();
      // Clear any leftover active agency from a prior session in this browser.
      // The newly-onboarded user has 0 memberships and is about to CREATE
      // their first agency — sending a stale X-Agency-Id would 403 the
      // create-agency request via ResolveAgencyContext.
      auth.setActiveAgency(null);
      auth.setAuth(
        response.data.user,
        response.data.token,
        null,
        null,
        null,
        null
      );
      set({ userData: response.data });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.message || "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  // Uses apiClient — user now has a token from sendUserData
  createMediaAgency: async (agencyData) => {
    try {
      const formData = new FormData();
      formData.append("name", agencyData.name);
      formData.append("website", agencyData.website);
      formData.append("phone", agencyData.phone);
      formData.append("industry", agencyData.industry);
      formData.append("size", agencyData.size);
      if (agencyData.logo) {
        formData.append("logo", agencyData.logo);
      }
      Object.entries(agencyData.shipping).forEach(([key, value]) => {
        formData.append(`shipping[${key}]`, value);
      });
      Object.entries(agencyData.billing).forEach(([key, value]) => {
        formData.append(`billing[${key}]`, value);
      });

      // No manual headers — apiClient interceptor handles token + FormData
      const response = await apiClient.post("/media-agency-information", formData);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.message || "Something went wrong";
      throw new Error(errorMessage);
    }
  },
}));

export default useOnboardingStore;
